/**
 * PATH: packages/ui/src/kanban/components/KanbanCardQuickActions.tsx
 * FILE_ID: UI-KANBAN-CARD-QUICKACTIONS-001
 * PURPOSE: The inline quick-action button rail rendered in the card-face hover
 *   zone — Priority / Due / Assign / Move / Archive — each a real button (with
 *   a small portaled picker where needed) instead of being buried behind `…`.
 * WHY:
 *   FEAT-2026-06-20-KANBAN-CARD-ACTION-RAIL. Operator (2026-06-21): *"the
 *   bottom expansion doesn't have all the features which we needed with
 *   kcards"*. The hover zone previously showed only the `…` menu trigger + the
 *   Thread pill, so every card action took two clicks (open menu → pick). This
 *   surfaces the high-frequency actions as one-click buttons, Trello-style.
 *   The handlers + datasets are the SAME ones the right-click menu uses (board
 *   wires both to one pipe — see KanbanCard.types KanbanCardQuickActionHandlers),
 *   so an inline edit and a menu edit are indistinguishable to the backend.
 *   FEAT-2026-07-02-KANBAN-CARD-ACTION-CAPSULE, operator: "this can be
 *   significantly better designed". The six loose ghost glyphs now render as
 *   ONE segmented capsule (border + faint fill), split by a hairline into a
 *   "plan" triad (priority/due/assign) and a "dispatch" triad (move/send/
 *   archive) so the row scans as two intents, matching the right-edge hover
 *   strip's capsule design language.
 * FLOW:
 *   ┌──────────────┐  click  ┌───────────────────────┐  pick  ┌──────────────┐
 *   │ rail button  │ ──────▶ │ portaled picker        │ ─────▶ │ host handler │
 *   │ (Flag/Cal/…) │         │ (priority/due/assign…) │        │ → mutation   │
 *   └──────────────┘         └───────────────────────┘        └──────────────┘
 *   Archive is one-tap (no picker). Each button + picker is gated on its
 *   handler (and, for assign/move, its data) being wired.
 * DEPENDENCIES:
 *   - lucide-react: the action glyphs.
 *   - ./KanbanCardQuickActionPickers: the portaled picker popover bodies.
 *   - ./KanbanCard.types: KanbanCardQuickActionHandlers contract.
 * RELATED:
 *   - ./KanbanCardActionZone.tsx: renders this as `quickActionsSlot`.
 *   - ./KanbanContextMenu.tsx: the right-click sibling (same option sets).
 */
import { useEffect, useRef, useState } from 'react'
import { Archive, CalendarClock, Flag, Move, Send, Table2, Target, UserPlus } from 'lucide-react'

import type { Card } from '../types'
import type { KanbanCardQuickActionHandlers } from './KanbanCard.types'
import {
  KanbanCardQuickActionPickers,
  type QuickActionOpenState,
  type QuickActionPickerKind,
} from './KanbanCardQuickActionPickers'
import { triggerRectFromElement } from './placeBesideTrigger'
import { takePendingAllocate } from './cardAllocateHotkey'

export interface KanbanCardQuickActionsProps extends KanbanCardQuickActionHandlers {
  card: Card
  /** Fired when a picker opens/closes so the card can suspend the hover strip. */
  onPickerOpenChange?: (open: boolean) => void
}

// FEAT-2026-07-02-KANBAN-CARD-ACTION-CAPSULE: the rail is ONE segmented
// capsule (border + faint fill + internal dividers), matching the right-edge
// hover strip's design language, instead of six loose ghost glyphs floating
// over the card ("this can be significantly better designed"). Buttons are
// fixed 24px squares so the row never re-rags as glyph widths differ.
//
// Hover feedback: the icon grows GENTLY while THAT specific button is hovered
// (operator 2026-06-23). BUG-2026-06-30-KANBAN-CARD-ACTION-OVERLAP: `scale-110`
// (not the old `scale-150`) keeps a hovered glyph inside its own box.
const RAIL_SHELL =
  'inline-flex items-center gap-px rounded-lg border border-neutral-200/80 bg-white/80 p-0.5 shadow-sm shadow-neutral-900/[0.04] dark:border-zinc-700/70 dark:bg-zinc-900/60 dark:shadow-black/20'
const BTN =
  'inline-flex h-6 w-6 items-center justify-center rounded-md text-neutral-500 transition-[transform,background-color,color] duration-150 hover:scale-110 hover:bg-neutral-100 hover:text-neutral-800 disabled:opacity-50 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-100'
const ICON = 'h-4 w-4'
// Hairline between the "plan" group (priority / due / assign) and the
// "dispatch" group (move / send / archive) so the six icons scan as two
// related triads instead of an undifferentiated icon soup.
const RAIL_DIVIDER = 'mx-0.5 h-3.5 w-px shrink-0 rounded-full bg-neutral-200/90 dark:bg-zinc-700/80'

/**
 * The hover-zone quick-action rail. Each button renders only when its handler
 * (and required data) is present, so the rail right-sizes per host. Returns
 * null when nothing is wired so the action zone collapses to `…` + Thread.
 */
export function KanbanCardQuickActions({
  card,
  onSetPriority,
  onSetDueDate,
  onSetActionDate,
  onAssign,
  onMoveTo,
  onAllocateToLane,
  onAllocateToBoard,
  onLoadBoards,
  onLoadBoardLanes,
  onMoveToBoard,
  currentBoardId,
  onToggleArchive,
  onComposeEmail,
  onOpenEntries,
  members,
  lanes,
  onPickerOpenChange,
}: KanbanCardQuickActionsProps) {
  const [open, setOpen] = useState<QuickActionOpenState | null>(null)
  const [busy, setBusy] = useState(false)
  const [assignBusyId, setAssignBusyId] = useState<number | null>(null)

  useEffect(() => {
    onPickerOpenChange?.(open != null)
  }, [open, onPickerOpenChange])

  useEffect(() => {
    const openAllocate = () => {
      const el = document.querySelector<HTMLElement>(
        `[data-card-id="${CSS.escape(card.id)}"]`,
      )
      if (!el) return
      setOpen({ kind: 'allocate', trigger: triggerRectFromElement(el) })
    }
    if (takePendingAllocate(card.id)) openAllocate()
    const onOpen = (event: Event) => {
      const cardId = (event as CustomEvent<{ cardId?: string }>).detail?.cardId
      if (cardId !== card.id) return
      takePendingAllocate(card.id)
      openAllocate()
    }
    window.addEventListener('kanban:open-card-allocate', onOpen)
    return () => window.removeEventListener('kanban:open-card-allocate', onOpen)
  }, [card.id])

  // FIX-2026-06-23-KANBAN-CARD-PICKER-DISMISS — operator: "it doesn't go away
  // when you move the mouse away from the people hover". The assign picker opens
  // on hover but only closed on an explicit outside click, so it stuck on screen
  // when the cursor moved off without clicking. Hover-bridge: leaving the rail OR
  // the popover arms a short close; entering either cancels it — so the picker
  // stays while the cursor is on the rail/popover and dismisses the moment it
  // moves away (small grace so a wobble back cancels).
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  function cancelClose() {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }
  function scheduleClose() {
    cancelClose()
    // 320ms grace (was 240): FEAT-2026-07-02-KANBAN-DUE-PICKER-LADDER,
    // operator: "meet it easier to hover over" - a slower pointer path from
    // the rail button down into the popover must not lose the picker.
    closeTimer.current = setTimeout(() => setOpen(null), 320)
  }
  // FEAT-2026-07-02-KANBAN-CARD-HOVER-EXPAND: EVERY picker action now opens on
  // hover (operator: "hovering over the action items ... should automatically
  // expand it same with other common sense actions"), not just assign. A short
  // dwell keeps a pointer TRAVERSING the rail from popping every picker in
  // sequence; leaving the button before the dwell elapses cancels the open.
  // One-tap actions (archive) stay click-only: hover must never mutate.
  const hoverOpenTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  function cancelHoverOpen() {
    if (hoverOpenTimer.current) {
      clearTimeout(hoverOpenTimer.current)
      hoverOpenTimer.current = null
    }
  }
  useEffect(
    () => () => {
      cancelClose()
      cancelHoverOpen()
    },
    [],
  )

  const showAssign = !!onAssign && (members?.length ?? 0) > 0
  const showMove = !!onMoveTo && (lanes?.length ?? 0) > 0
  const showAllocate = !!onAllocateToLane || !!onAllocateToBoard || !!onMoveToBoard
  const showCompose = !!onComposeEmail
  const isArchived = card.status === 'ARCHIVED'

  // Two scannable triads: "plan" (what/when/who) and "dispatch" (where/out/away).
  // The divider only renders when BOTH groups produced at least one button.
  const hasPlanGroup =
    !!onSetPriority || !!onSetDueDate || !!onSetActionDate || showAssign || !!onOpenEntries
  const hasDispatchGroup = showMove || showAllocate || showCompose || !!onToggleArchive

  if (!hasPlanGroup && !hasDispatchGroup) {
    return null
  }

  // Capture the button's full viewport rect so the portaled picker opens
  // BESIDE the icon (placeBesideTrigger) — never over the action rail.
  function openPicker(kind: QuickActionPickerKind, e: React.MouseEvent) {
    e.stopPropagation()
    e.preventDefault()
    cancelHoverOpen()
    const trigger = triggerRectFromElement(e.currentTarget)
    setOpen((prev) => (prev?.kind === kind ? null : { kind, trigger }))
  }

  // FEAT-2026-06-23 (assign) generalised by FEAT-2026-07-02-KANBAN-CARD-
  // HOVER-EXPAND to every picker: hover ARMS an open after a short dwell.
  // Hover only OPENS (never toggles closed), so grazing won't flicker an
  // already-open picker; click still toggles. The hover-bridge (closeTimer)
  // dismisses it when the cursor leaves the rail AND the popover.
  function openPickerOnHover(kind: QuickActionPickerKind, e: React.MouseEvent) {
    const trigger = triggerRectFromElement(e.currentTarget)
    cancelHoverOpen()
    hoverOpenTimer.current = setTimeout(() => {
      setOpen((prev) => (prev?.kind === kind ? prev : { kind, trigger }))
    }, 130)
  }

  // Run a one-shot action (priority/due/move/archive) then close the picker.
  async function run(fn: () => Promise<unknown> | void) {
    setBusy(true)
    try {
      await fn()
    } finally {
      setBusy(false)
      setOpen(null)
    }
  }

  async function handleAssignToggle(userId: number, willSelect: boolean) {
    if (!onAssign) return
    setAssignBusyId(userId)
    try {
      await onAssign(card, userId, willSelect)
    } finally {
      setAssignBusyId(null)
    }
  }

  return (
    <div
      className={RAIL_SHELL}
      data-card-quick-actions=""
      // Belt-and-braces: the zone already stops drag, but the rail's own
      // pointer-down must never bubble to the card root's drag/open handlers.
      onMouseDown={(e) => e.stopPropagation()}
      // Hover-bridge (see closeTimer above): being on the rail keeps an open
      // picker alive; leaving it arms the close (cancelled if the cursor lands
      // on the portaled popover, which shares this timer via onHoverIn/Out).
      onMouseEnter={cancelClose}
      onMouseLeave={() => {
        cancelHoverOpen()
        scheduleClose()
      }}
    >
      {onSetPriority && (
        <button
          type="button"
          data-card-action="priority"
          aria-label="Set priority"
          title="Set priority"
          onMouseDown={(e) => e.stopPropagation()}
          onMouseEnter={(e) => openPickerOnHover('priority', e)}
          onMouseLeave={cancelHoverOpen}
          onClick={(e) => openPicker('priority', e)}
          className={BTN}
        >
          <Flag className={ICON} aria-hidden="true" />
        </button>
      )}
      {onSetDueDate && (
        <button
          type="button"
          data-card-action="due"
          aria-label="Set due date"
          title="Set due date"
          onMouseDown={(e) => e.stopPropagation()}
          onMouseEnter={(e) => openPickerOnHover('due', e)}
          onMouseLeave={cancelHoverOpen}
          onClick={(e) => openPicker('due', e)}
          className={BTN}
        >
          <CalendarClock className={ICON} aria-hidden="true" />
        </button>
      )}
      {onSetActionDate && (
        <button
          type="button"
          data-card-action="action-date"
          aria-label="Set action date"
          title="Set action date"
          onMouseDown={(e) => e.stopPropagation()}
          onMouseEnter={(e) => openPickerOnHover('action', e)}
          onMouseLeave={cancelHoverOpen}
          onClick={(e) => openPicker('action', e)}
          className={BTN}
        >
          <Target className={ICON} aria-hidden="true" />
        </button>
      )}
      {showAssign && (
        <button
          type="button"
          data-card-action="assign"
          aria-label="Assign members"
          title="Assign members"
          onMouseDown={(e) => e.stopPropagation()}
          onMouseEnter={(e) => openPickerOnHover('assign', e)}
          onMouseLeave={cancelHoverOpen}
          onClick={(e) => openPicker('assign', e)}
          className={BTN}
        >
          <UserPlus className={ICON} aria-hidden="true" />
        </button>
      )}
      {onOpenEntries && (
        <button
          type="button"
          data-card-action="entries"
          aria-label="Open entries table"
          title="Entries — quick sub-card table (AI can draft the rows)"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            // One-tap like archive: opens the drawer's Entries table —
            // hover must never open a drawer, so no hover-arm here.
            e.stopPropagation()
            e.preventDefault()
            onOpenEntries(card)
          }}
          className={BTN}
        >
          <Table2 className={ICON} aria-hidden="true" />
        </button>
      )}
      {hasPlanGroup && hasDispatchGroup && (
        <span aria-hidden="true" className={RAIL_DIVIDER} />
      )}
      {showMove && (
        <button
          type="button"
          data-card-action="move"
          aria-label="Move to lane"
          title="Move to lane"
          onMouseDown={(e) => e.stopPropagation()}
          onMouseEnter={(e) => openPickerOnHover('move', e)}
          onMouseLeave={cancelHoverOpen}
          onClick={(e) => openPicker('move', e)}
          className={BTN}
        >
          <Move className={ICON} aria-hidden="true" />
        </button>
      )}
      {showAllocate && (
        <button
          type="button"
          data-card-action="allocate"
          aria-label="Allocate"
          title="Allocate, or move to another board"
          onMouseDown={(e) => {
            // Open on press. The card is draggable, so the browser often
            // cancels the later click and the picker never appears.
            e.stopPropagation()
            e.preventDefault()
            cancelClose()
            cancelHoverOpen()
            setOpen({
              kind: 'allocate',
              trigger: triggerRectFromElement(e.currentTarget),
            })
          }}
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            cancelClose()
            setOpen({
              kind: 'allocate',
              trigger: triggerRectFromElement(e.currentTarget),
            })
          }}
          className={BTN}
        >
          <span className="text-[11px] font-semibold leading-none">A</span>
        </button>
      )}
      {showCompose && (
        <button
          type="button"
          data-card-action="compose"
          aria-label="Send card as email"
          title="Send card as email"
          onMouseDown={(e) => e.stopPropagation()}
          onMouseEnter={(e) => openPickerOnHover('compose', e)}
          onMouseLeave={cancelHoverOpen}
          onClick={(e) => openPicker('compose', e)}
          className={BTN}
        >
          <Send className={ICON} aria-hidden="true" />
        </button>
      )}
      {onToggleArchive && (
        <button
          type="button"
          data-card-action="archive"
          aria-label={isArchived ? 'Restore card' : 'Archive card'}
          title={isArchived ? 'Restore card' : 'Archive card'}
          disabled={busy}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            void run(() => onToggleArchive(card))
          }}
          className={BTN}
        >
          <Archive className={ICON} aria-hidden="true" />
        </button>
      )}

      {open && (
        <KanbanCardQuickActionPickers
          open={open}
          card={card}
          busy={busy}
          assignBusyId={assignBusyId}
          run={(fn) => void run(fn)}
          onAssignToggle={handleAssignToggle}
          onComposeEmail={
            onComposeEmail ? (recipients) => onComposeEmail(card, recipients) : undefined
          }
          onClose={() => setOpen(null)}
          onHoverIn={cancelClose}
          onHoverOut={scheduleClose}
          handlers={{
            onSetPriority,
            onSetDueDate,
            onSetActionDate,
            onAssign,
            onMoveTo,
            onAllocateToLane,
            onAllocateToBoard,
            onLoadBoards,
            onLoadBoardLanes,
            onMoveToBoard,
            currentBoardId,
            members,
            lanes,
          }}
        />
      )}
    </div>
  )
}
